/**
 * pipelineDonutView.ts — the pure half of the Sales Home pipeline donut
 * (redesign slice 3, the Minimal board's ring; ADOPTION.md §15).
 *
 * The wire is the server's fixed 5-row spine (open stages + closed_won,
 * zero-filled) that funnelRows already models. The fills are BOUND by the
 * spine's fixed position BEFORE any zero slice is dropped (CLAUDE.md chart
 * gotcha: "bind to each category's fixed palette slot BEFORE zero slices are
 * dropped, or the palette reshuffles as data populates") — funnelRows binds
 * them, this file only reads them. The four open rows are a STOCK (a
 * distribution of deals sitting in each stage now — C-6), which is what a
 * part-of-whole ring can show honestly; Closed Won stays out of the ring as
 * the all-time flow it is (C-7) and rides beneath it as a count.
 *
 * Geometry: the boards' 132 px ring. A 2 px gap in the SURFACE colour
 * separates touching arcs (the dataviz mark spec) — the ring draws arcs only,
 * so the card shows through the gaps; an all-zero pipeline draws one track.
 */
import { funnelRows, CLOSED_WON_STAGE, type FunnelCount, type FunnelRow } from './funnelRows'

export const RING = { box: 132, radius: 54, stroke: 14, gap: 2 } as const
const C = 2 * Math.PI * RING.radius

export interface DonutArc {
  stage: string; label: string; count: number; fill: string
  /** of the open total, 0–100 */
  pct: number
  /** SVG stroke geometry for a circle of RING.radius */
  dashArray: string; dashOffset: number
  /** the hover title: "New Lead · 4 of 9" */
  title: string
}

export interface DonutModel {
  openTotal: number
  /** every open stage, zero rows INCLUDED, in spine order, fill bound */
  legend: { stage: string; label: string; count: number; fill: string }[]
  /** only the non-zero stages, drawn in spine order */
  arcs: DonutArc[]
  won: { stage: string; label: string; count: number } | null
}

export function donutModel(funnel: FunnelCount[]): DonutModel {
  const rows = funnelRows(funnel)
  const open = rows.filter((r): r is Extract<FunnelRow, { kind: 'open' }> => r.kind === 'open')
  const wonRow = rows.find((r): r is Extract<FunnelRow, { kind: 'won' }> => r.kind === 'won')
  const openTotal = open.reduce((s, r) => s + r.count, 0)
  const legend = open.map(r => ({ stage: r.stage, label: r.label, count: r.count, fill: r.fill }))
  const arcs: DonutArc[] = []
  let offset = 0
  for (const r of open) {
    if (r.count === 0) continue
    const len = (r.count / openTotal) * C
    const drawn = Math.max(0, len - RING.gap)
    arcs.push({
      stage: r.stage, label: r.label, count: r.count, fill: r.fill, pct: Math.round((r.count / openTotal) * 100),
      dashArray: `${drawn} ${C - drawn}`, dashOffset: -offset,
      title: `${r.label} · ${r.count} of ${openTotal}`,
    })
    offset += len
  }
  return { openTotal, legend, arcs, won: wonRow ? { stage: wonRow.stage, label: wonRow.label, count: wonRow.count } : null }
}

export { CLOSED_WON_STAGE }
